import { World } from './world'
import { Renderer } from './renderer'
import type { Organism } from './organism'
import type { PopulationConfig } from './types'

const canvas = document.getElementById('canvas') as HTMLCanvasElement
let world: World
let renderer: Renderer
let lastTime = 0
let fps = 60
let running = false
let speedMultiplier = 1
let selectedOrganism: Organism | null = null

// 設定UI要素
const setupOverlay = document.getElementById('setup-overlay')!
const sliderHerb = document.getElementById('slider-herb') as HTMLInputElement
const sliderCarn = document.getElementById('slider-carn') as HTMLInputElement
const sliderOmni = document.getElementById('slider-omni') as HTMLInputElement
const valHerb = document.getElementById('val-herb')!
const valCarn = document.getElementById('val-carn')!
const valOmni = document.getElementById('val-omni')!
const valTotal = document.getElementById('val-total')!

function updateSetupLabels(): void {
  valHerb.textContent = sliderHerb.value
  valCarn.textContent = sliderCarn.value
  valOmni.textContent = sliderOmni.value
  valTotal.textContent = String(
    parseInt(sliderHerb.value) + parseInt(sliderCarn.value) + parseInt(sliderOmni.value)
  )
}
sliderHerb.addEventListener('input', updateSetupLabels)
sliderCarn.addEventListener('input', updateSetupLabels)
sliderOmni.addEventListener('input', updateSetupLabels)

function getPopConfig(): PopulationConfig {
  return {
    herbivores: parseInt(sliderHerb.value),
    carnivores: parseInt(sliderCarn.value),
    omnivores: parseInt(sliderOmni.value),
  }
}

function showSetup(): void {
  running = false
  setupOverlay.classList.remove('hidden')
}

function resize(): void {
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  if (renderer) renderer.resize(canvas.width, canvas.height)
}

function startSimulation(popConfig: PopulationConfig): void {
  resize()
  world = new World(canvas.width, canvas.height, popConfig)
  renderer = new Renderer(canvas)
  selectedOrganism = null
  setupOverlay.classList.add('hidden')
  running = true
  // 一時停止ボタンをリセット
  const btn = document.getElementById('btn-pause')
  if (btn) btn.textContent = '⏸ 一時停止'
  lastTime = performance.now()
  requestAnimationFrame(loop)
}

// 開始ボタン
document.getElementById('btn-start')?.addEventListener('click', () => {
  startSimulation(getPopConfig())
})

function loop(now: number): void {
  if (!running) return
  requestAnimationFrame(loop)

  const rawDt = Math.min((now - lastTime) / 1000, 0.05)
  lastTime = now

  fps = fps * 0.9 + (1 / (rawDt || 0.016)) * 0.1

  const dt = rawDt * speedMultiplier
  world.update(dt)

  // 選択中の個体が死んでいたらクリア
  if (selectedOrganism && !world.organisms.find(o => o.id === selectedOrganism!.id)) {
    selectedOrganism = null
  }

  renderer.render(world, selectedOrganism)
  updateStatsPanel()
  updateDetailPanel()
}

function updateStatsPanel(): void {
  const s = world.stats
  const el = document.getElementById('stats-panel')
  if (!el) return
  el.innerHTML = `
    <div class="stat-row"><span class="label">FPS</span><span class="value">${fps.toFixed(0)}</span></div>
    <div class="stat-row"><span class="label">Tick</span><span class="value">${s.tick}</span></div>
    <div class="stat-row"><span class="label">最大世代</span><span class="value">${s.maxGeneration}</span></div>
    <hr class="stat-divider">
    <div class="stat-row"><span class="label">総個体数</span><span class="value">${s.organisms}</span></div>
    <div class="stat-row"><span class="label herb">草食</span><span class="value">${s.herbivores}</span></div>
    <div class="stat-row"><span class="label carn">肉食</span><span class="value">${s.carnivores}</span></div>
    <div class="stat-row"><span class="label omni">雑食</span><span class="value">${s.omnivores}</span></div>
    <div class="stat-row"><span class="label">食料数</span><span class="value">${s.foods}</span></div>
  `
}

function updateDetailPanel(): void {
  const panel = document.getElementById('detail-panel')
  if (!panel) return
  if (!selectedOrganism) {
    panel.style.display = 'none'
    return
  }
  const o = world.organisms.find(x => x.id === selectedOrganism!.id)
  if (!o) {
    panel.style.display = 'none'
    selectedOrganism = null
    return
  }
  const t = o.traits
  const dietLabel = t.diet === 'herbivore' ? '草食' : t.diet === 'carnivore' ? '肉食' : '雑食'
  panel.style.display = 'block'
  panel.innerHTML = `
    <div class="detail-title">個体 #${o.id}</div>
    <div class="stat-row"><span class="label">食性</span><span class="value">${dietLabel}</span></div>
    <div class="stat-row"><span class="label">世代</span><span class="value">${o.generation}</span></div>
    <div class="stat-row"><span class="label">親ID</span><span class="value">${o.parentId ?? '—'}</span></div>
    <div class="stat-row"><span class="label">エネルギー</span><span class="value">${o.energy.toFixed(1)}</span></div>
    <div class="stat-row"><span class="label">年齢</span><span class="value">${o.age.toFixed(1)}s</span></div>
    <hr class="stat-divider">
    <div class="stat-row"><span class="label">速度</span><span class="value">${t.speed.toFixed(2)}</span></div>
    <div class="stat-row"><span class="label">視野</span><span class="value">${t.visionRange.toFixed(1)}</span></div>
    <div class="stat-row"><span class="label">サイズ</span><span class="value">${t.size.toFixed(2)}</span></div>
    <div class="stat-row"><span class="label">代謝</span><span class="value">${t.metabolismRate.toFixed(2)}</span></div>
    <button id="btn-deselect" style="margin-top:8px;width:100%">✕ 選択解除</button>
  `
  document.getElementById('btn-deselect')?.addEventListener('click', () => {
    selectedOrganism = null
  })
}

// クリックで個体選択
canvas.addEventListener('click', (e) => {
  const rect = canvas.getBoundingClientRect()
  const mx = e.clientX - rect.left
  const my = e.clientY - rect.top

  let closest: Organism | null = null
  let closestDist = 20  // クリック判定半径

  for (const org of world.organisms) {
    const dx = org.pos.x - mx
    const dy = org.pos.y - my
    const d = Math.sqrt(dx * dx + dy * dy)
    if (d < closestDist) {
      closestDist = d
      closest = org
    }
  }

  selectedOrganism = closest
})

// コントロール
document.getElementById('btn-pause')?.addEventListener('click', () => {
  running = !running
  const btn = document.getElementById('btn-pause')!
  btn.textContent = running ? '⏸ 一時停止' : '▶ 再開'
  if (running) {
    lastTime = performance.now()
    requestAnimationFrame(loop)
  }
})

document.getElementById('btn-reset')?.addEventListener('click', () => {
  showSetup()
})

// 速度スライダー
const speedSlider = document.getElementById('speed-slider') as HTMLInputElement | null
const speedLabel = document.getElementById('speed-label')
speedSlider?.addEventListener('input', () => {
  speedMultiplier = parseFloat(speedSlider.value)
  if (speedLabel) speedLabel.textContent = `×${speedMultiplier.toFixed(1)}`
})

window.addEventListener('resize', () => {
  resize()
  if (running && world) {
    world = new World(canvas.width, canvas.height, getPopConfig())
    selectedOrganism = null
  }
})

// 初期状態: 設定画面を表示
resize()
